<script setup lang="ts">
import BaseModal from '@/components/modals/BaseModal.vue';
import { useUserStore } from '@/services/auth';
import { useApiEnvStore } from '@/services/env';
import { User } from '@/types/user';
import { onMounted, ref } from 'vue';

// Test Code
async function executeTest() {
	try {
		const response = await fetch('https://us-central1-mintech-ecosystem.cloudfunctions.net/ListAllAcumaticaStockItems', {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${await userStore.user?.getIdToken()}`
			}
		});
		const jsonResponse = await response.json();
		console.log(jsonResponse);
		alert('Function executed successfully. Check console for details.');
	} catch (error) {
		alert('Error executing test function. Please try again later.');
	}
}

const userStore = useUserStore(); // access user store for auth token
const users = ref<User[]>([]); // list of users
const status = ref<string>(''); // status message displayed at the bottom of the page
const isManagingUser = ref<boolean>(false); // modal visibility
const selectedUser = ref<User | null>(null); // currently selected user for management
const selectedUserRoles = ref<string>(''); // roles of the selected user as a comma-separated string

/**
 * Opens the manage user modal for the specified user.
 * Adds the user's current roles to the input field.
 *
 * @param user
 */
function openManageUserModal(user: User) {
	selectedUser.value = user;
	selectedUserRoles.value = user.roles.join(', ');
	isManagingUser.value = true;
}

/**
 * Closes the manage user modal and resets selected user data.
 */
function closeManageUserModal() {
	selectedUser.value = null;
	selectedUserRoles.value = '';
	isManagingUser.value = false;
}

/**
 * Loads all users from the backend service.
 * Updates the users list and status message accordingly.
 */
async function loadUsers() {
	status.value = 'Loading users...';
	try {
		let userData: User[] = [];
		const envStore = useApiEnvStore();
		const response = await fetch(`${envStore.apiBaseUrl}/GetAllUsers`, {
			method: 'GET',
			headers: {
				Authorization: `Bearer ${await userStore.user?.getIdToken()}`
			}
		});

		if (!response.ok) {
			throw new Error();
		}

		const data = await response.json();

		userData = data.map((user: any) => ({
			uid: user.uid,
			email: user.email,
			displayName: user.displayName,
			roles: user.roles || []
		}));

		users.value = userData;
		status.value = 'Users loaded successfully.';
	} catch (error) {
		alert('Error loading users. Please try again later.');
		status.value = 'Error loading users.';
	}
}

async function updateUser() {
	if (!selectedUser.value) return;
	status.value = 'Updating user...';

	try {
		const envStore = useApiEnvStore();
		const response = await fetch(`${envStore.apiBaseUrl}/UpdateUserClaims`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${await userStore.user?.getIdToken()}`
			},
			body: JSON.stringify({
				uid: selectedUser.value.uid,
				roles: selectedUserRoles.value.split(',').map((role) => role.trim())
			})
		});

		if (!response.ok) {
			throw new Error();
		}

		closeManageUserModal();
		status.value = 'User updated successfully.';
		await loadUsers();
	} catch (error) {
		alert('Error updating user. Please try again later.');
		status.value = 'Error updating user.';
	}
}

onMounted(async () => {
	await loadUsers();
});
</script>

<template>
	<div class="users-view">
		<!-- Header -->
		<div class="users-view__header">
			<h1 class="fira aliceblue">User Management</h1>
		</div>

		<!-- Content -->
		<div class="users-view__content">
			<p class="fira aliceblue users-header">Users</p>
			<div class="content__table-header">
				<table>
					<thead>
						<tr>
							<th>Full Name</th>
							<th>Email</th>
							<th>Roles</th>
						</tr>
					</thead>
				</table>
			</div>
			<div class="content__table-container">
				<table>
					<tbody>
						<tr v-for="user in users" :key="user.email" @dblclick="openManageUserModal(user)">
							<td>{{ user.displayName }}</td>
							<td>{{ user.email }}</td>
							<td>{{ user.roles.join(', ') }}</td>
						</tr>
					</tbody>
				</table>
			</div>

			<!-- Status -->
			<p class="status-indicator" v-if="status">{{ status }}</p>
			<<<<<<< HEAD =======
			<button @click="executeTest">Test</button>
			>>>>>>> 09b51b0 (test setup)
		</div>

		<!-- User Modal -->
		<BaseModal v-if="isManagingUser && selectedUser" @close="closeManageUserModal">
			<div class="manage-user-modal">
				<p class="bold">{{ selectedUser.displayName }}</p>
				<div class="manage-user-modal__user-fields">
					<p>Email:</p>
					<p>{{ selectedUser.email }}</p>
					<p>Roles:</p>
					<input class="modal" v-model="selectedUserRoles" />
				</div>
				<div class="manage-user-modal__buttons">
					<button @click="updateUser">Update User</button>
					<button @click="closeManageUserModal">Close</button>
				</div>
			</div>
		</BaseModal>
	</div>
</template>

<style scoped>
.users-view {
	height: 100vh;
	display: flex;
	flex-direction: column;
	gap: 15px;
	position: relative;
	user-select: none;
	overflow-y: hidden;
}

h1 {
	font-weight: normal;
	margin-left: 15px;
	margin-top: 15px;
	font-size: 1.5rem;
}

.users-header {
	font-size: 1.25rem;
	margin-left: 15px;
}

.users-view__content {
	display: flex;
	flex-direction: column;
	flex: 1;
	overflow-y: hidden;
	margin-bottom: 15px;
}

.users-view__footer {
	display: flex;
	margin-left: 15px;
	margin-bottom: 15px;
}

.modal-content {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

/* Table Containers */

.content__table-header {
	background: #4b525a;
	border-radius: 5px 5px 0 0;
	margin-top: 5px;
	width: 98%;
	align-self: center;
}

.content__table-container {
	max-height: 100%;
	overflow-y: auto;
	background-color: #4b525a;
	flex: 1;
	width: 98%;
	align-self: center;
	border-radius: 0 0 5px 5px;
}

/* Table */

table {
	width: 100%;
	border-collapse: collapse;
	background: transparent;
	font-family: 'Fira Sans', serif;
	flex: 1;
	table-layout: fixed;
}

thead {
	border-bottom: 1px solid #272b2f;
	text-align: left;
	color: aliceblue;
	top: 0;
	position: sticky;
	opacity: 100;
}

thead th {
	padding: 20px 30px;
	font-weight: normal;
}

.max-col {
	width: auto;
}

tbody {
	max-height: 100%;
	overflow-y: auto;
}

tbody td {
	padding: 20px 30px;
	white-space: pre-line;
	font-weight: 200;
	color: aliceblue;
}

tbody tr:hover {
	background: #646e79;
	cursor: pointer;
}

tbody tr.selected {
	background: #646e79;
}

/* Status Indicator */
.status-indicator {
	font-family: 'Fira Sans';
	color: #1b5299;
	margin: 8px 0 0 15px;
}

/* Manage User Modal */
.manage-user-modal {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.manage-user-modal__user-fields {
	display: grid;
	grid-template-columns: max-content 1fr;
	column-gap: 8px;
	row-gap: 5px;
}

.manage-user-modal__buttons {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
}
</style>
